/**
 * Point d’entrée des composants Web Awesome utilisés par le Manager.
 *
 * Le bundle enregistre explicitement chaque Web Component — un élément HTML
 * autonome dont l’implémentation est encapsulée — puis installe uniquement les
 * adaptations transversales nécessaires aux contrats serveur existants.
 *
 * Les feuilles de style WA sont chargées séparément par le layout Manager. Ne
 * pas les réimporter ici évite que la compilation JavaScript les réécrive.
 */
import WaButton from '@awesome.me/webawesome/dist/components/button/button.js';
import WaAvatar from '@awesome.me/webawesome/dist/components/avatar/avatar.js';
import WaBadge from '@awesome.me/webawesome/dist/components/badge/badge.js';
import WaBreadcrumb from '@awesome.me/webawesome/dist/components/breadcrumb/breadcrumb.js';
import WaBreadcrumbItem from '@awesome.me/webawesome/dist/components/breadcrumb-item/breadcrumb-item.js';
import WaCard from '@awesome.me/webawesome/dist/components/card/card.js';
import WaDropdown from '@awesome.me/webawesome/dist/components/dropdown/dropdown.js';
import WaDropdownItem from '@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item.js';
import WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import WaDetails from '@awesome.me/webawesome/dist/components/details/details.js';
import WaDrawer from '@awesome.me/webawesome/dist/components/drawer/drawer.js';
import WaIcon from '@awesome.me/webawesome/dist/components/icon/icon.js';
import WaInput from '@awesome.me/webawesome/dist/components/input/input.js';
import WaOption from '@awesome.me/webawesome/dist/components/option/option.js';
import WaPagination from '@awesome.me/webawesome/dist/components/pagination/pagination.js';
import WaSelect from '@awesome.me/webawesome/dist/components/select/select.js';
import WaTab from '@awesome.me/webawesome/dist/components/tab/tab.js';
import WaTabGroup from '@awesome.me/webawesome/dist/components/tab-group/tab-group.js';
import WaTabPanel from '@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js';
import WaTooltip from '@awesome.me/webawesome/dist/components/tooltip/tooltip.js';
import './image-picker.js';

// L'enregistrement explicite garde le bundle commun déterministe : les pages
// ne chargent que les composants Web Awesome retenus par l'interface, sans
// importer automatiquement tout le catalogue de composants.
for (const [tagName, component] of [
    ['wa-avatar', WaAvatar],
    ['wa-badge', WaBadge],
    ['wa-breadcrumb', WaBreadcrumb],
    ['wa-breadcrumb-item', WaBreadcrumbItem],
    ['wa-button', WaButton],
    ['wa-card', WaCard],
    ['wa-dropdown', WaDropdown],
    ['wa-dropdown-item', WaDropdownItem],
    ['wa-dialog', WaDialog],
    ['wa-details', WaDetails],
    ['wa-drawer', WaDrawer],
    ['wa-icon', WaIcon],
    ['wa-input', WaInput],
    ['wa-option', WaOption],
    ['wa-pagination', WaPagination],
    ['wa-select', WaSelect],
    ['wa-tab', WaTab],
    ['wa-tab-group', WaTabGroup],
    ['wa-tab-panel', WaTabPanel],
    ['wa-tooltip', WaTooltip],
]) {
    if (!customElements.get(tagName)) customElements.define(tagName, component);
}

// Les définitions étant synchrones, deux trames suffisent pour laisser les
// composants calculer leurs dimensions avant de révéler le contenu Manager.
// Le layout possède en plus un délai de secours indépendant de ce bundle.
requestAnimationFrame(() => requestAnimationFrame(() => {
    document.documentElement.classList.remove('wa-components-loading');
}));

/**
 * Remplace l'icône générique d'une LOV par le média de sa valeur courante.
 *
 * L'option conserve un simple attribut `data-lov-image` : le composant commun
 * reste donc indépendant des personnes, chiens, clubs ou futurs référentiels.
 * Une URL absente ou une image en erreur restaure immédiatement le pictogramme.
 *
 * @param {HTMLElement} select Sélecteur Web Awesome déjà défini.
 * @returns {void}
 */
const synchronizeLovVisual = select => {
    if (select.hasAttribute('multiple')) return;

    const option = [...select.querySelectorAll('wa-option')]
        .find(candidate => String(candidate.value ?? candidate.getAttribute('value')) === String(select.value ?? ''));
    const imageUrl = option?.dataset.lovImage?.trim() ?? '';
    const fallback = [...select.children]
        .find(child => child.getAttribute('slot') === 'start' && !child.classList.contains('manager-lov-selected-visual'));
    let visual = select.querySelector(':scope > .manager-lov-selected-visual');

    if (imageUrl === '') {
        visual?.remove();
        fallback?.removeAttribute('hidden');
        select.classList.remove('manager-lov--visual');
        return;
    }

    if (visual === null) {
        visual = document.createElement('img');
        visual.className = 'manager-lov-selected-visual';
        visual.slot = 'start';
        visual.alt = '';
        visual.addEventListener('error', () => {
            visual.remove();
            fallback?.removeAttribute('hidden');
            select.classList.remove('manager-lov--visual');
        }, { once: true });
        select.append(visual);
    }

    visual.src = imageUrl;
    fallback?.setAttribute('hidden', '');
    select.classList.add('manager-lov--visual');
};

customElements.whenDefined('wa-select').then(() => {
    document.querySelectorAll('wa-select.manager-lov').forEach(select => {
        synchronizeLovVisual(select);
        select.addEventListener('change', () => synchronizeLovVisual(select));
    });
});

/**
 * Ajoute le chevron attendu à tout bouton qui déclenche un menu déroulant.
 *
 * @param {ParentNode} root Fragment de document dans lequel chercher.
 * @returns {void}
 */
const exposeDropdownCaret = root => {
    root.querySelectorAll('wa-dropdown > wa-button[slot="trigger"]:not(.account-menu-trigger)').forEach(button => button.setAttribute('with-caret', ''));
};

// Les vues peuvent rendre leurs dropdowns après le bundle commun. Le premier
// passage couvre le HTML initial, puis l'observateur traite uniquement les
// fragments ajoutés dynamiquement sans imposer de convention à chaque vue.
exposeDropdownCaret(document);
new MutationObserver(mutations => {
    for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
            if (node instanceof Element) {
                if (node.matches('wa-dropdown > wa-button[slot="trigger"]:not(.account-menu-trigger)')) node.setAttribute('with-caret', '');
                exposeDropdownCaret(node);
            }
        }
    }
}).observe(document.documentElement, { childList: true, subtree: true });

// Les entrées du menu utilisateur transmettent une URL comme valeur. La
// navigation reste centralisée ici, y compris pour la route de déconnexion,
// sans imbriquer de lien interactif dans un élément de menu interactif.
document.querySelectorAll('wa-dropdown[data-navigation]').forEach(dropdown => {
    dropdown.addEventListener('wa-select', event => {
        const url = event.detail?.item?.value;
        if (typeof url === 'string' && url !== '') window.location.assign(url);
    });
});

/** Met à jour tous les formulaires encore affichés après la régénération CSRF. */
const refreshCsrfTokens = csrf => {
    if (typeof csrf?.name !== 'string' || typeof csrf?.hash !== 'string') return;
    document.querySelectorAll(`input[name="${CSS.escape(csrf.name)}"]`).forEach(input => {
        input.value = csrf.hash;
        input.defaultValue = csrf.hash;
    });
};

/** Construit un contrôle à partir du contrat borné envoyé par le serveur. */
const createEntityEditField = field => {
    const group = document.createElement("div");
    const label = document.createElement("label");
    const controlId = `manager-entity-edit-${field.name}`;
    label.className = "form-label";
    label.htmlFor = controlId;
    label.textContent = field.label;
    group.append(label);

    let control;
    if (field.type === "select") {
        control = document.createElement("select");
        control.className = "form-select";
        const emptyOption = document.createElement("option");
        emptyOption.value = "";
        emptyOption.textContent = "Non renseigné";
        control.append(emptyOption);
        for (const optionData of field.options ?? []) {
            const option = document.createElement("option");
            option.value = optionData.value;
            option.textContent = optionData.label;
            control.append(option);
        }
    } else {
        control = document.createElement("input");
        control.className = "form-control";
        control.type = field.type;
        if (Number.isInteger(field.maxlength)) control.maxLength = field.maxlength;
    }
    control.id = controlId;
    control.name = field.name;
    control.required = field.required === true;
    control.value = field.value ?? "";
    group.append(control);

    return group;
};

const entityEditDrawer = document.querySelector("[data-entity-edit-drawer]");
const entityEditForm = entityEditDrawer?.querySelector("[data-entity-edit-form]") ?? null;

/** Ouvre partout le même drawer et charge une projection fraîche de la fiche. */
document.addEventListener("click", async event => {
    const trigger = event.target.closest("[data-entity-edit]");
    if (trigger === null || entityEditDrawer === null || entityEditForm === null) return;
    event.preventDefault();
    const baseUrl = entityEditDrawer.dataset.baseUrl.replace(/\/$/, "");
    const entityUrl = `${baseUrl}/${encodeURIComponent(trigger.dataset.entityKind)}/${encodeURIComponent(trigger.dataset.entityPublicId)}`;
    const errorBox = entityEditForm.querySelector("[data-entity-edit-errors]");
    const fields = entityEditForm.querySelector("[data-entity-edit-fields]");
    errorBox.classList.add("d-none");
    fields.replaceChildren();
    entityEditDrawer.label = "Chargement…";
    entityEditDrawer.open = true;

    try {
        const response = await fetch(entityUrl, { headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" } });
        const payload = await response.json();
        refreshCsrfTokens(payload.csrf);
        if (!response.ok || payload.success !== true) throw new Error(Object.values(payload.errors ?? {}).join(" ") || "La fiche n’a pas pu être chargée.");
        entityEditDrawer.label = `Modifier — ${payload.entity.title}`;
        entityEditForm.action = entityUrl;
        fields.replaceChildren(...payload.entity.fields.map(createEntityEditField));
    } catch (error) {
        errorBox.textContent = error.message;
        errorBox.classList.remove("d-none");
        errorBox.focus();
    }
});

entityEditForm?.addEventListener("submit", async event => {
    event.preventDefault();
    const errorBox = entityEditForm.querySelector("[data-entity-edit-errors]");
    const submitButton = entityEditDrawer.querySelector("[data-entity-edit-submit]");
    errorBox.classList.add("d-none");
    submitButton.disabled = true;
    try {
        const response = await fetch(entityEditForm.action, {
            method: "POST",
            body: new FormData(entityEditForm),
            headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        });
        const payload = await response.json();
        refreshCsrfTokens(payload.csrf);
        if (!response.ok || payload.success !== true) throw new Error(Object.values(payload.errors ?? {}).join(" ") || "La fiche n’a pas pu être modifiée.");
        document.querySelectorAll(`[data-entity-edit][data-entity-kind="${CSS.escape(payload.entity.kind)}"][data-entity-public-id="${CSS.escape(payload.entity.publicId)}"]`).forEach(trigger => {
            const label = trigger.querySelector("[data-entity-edit-label]");
            if (label !== null) label.textContent = payload.entity.title;
        });
        entityEditDrawer.open = false;
    } catch (error) {
        errorBox.textContent = error.message;
        errorBox.classList.remove("d-none");
        errorBox.focus();
    } finally {
        submitButton.disabled = false;
    }
});

// Un déclencheur ne connaît que l'identifiant de son drawer. Cette délégation
// permet aux vues d'ajouter de nouveaux cas sans recopier de gestionnaire JS.
document.addEventListener('click', event => {
    const opener = event.target.closest('[data-entity-drawer-open]');
    if (opener !== null) {
        const drawer = document.getElementById(opener.dataset.entityDrawerOpen);
        if (drawer !== null) drawer.open = true;
        return;
    }

    const closer = event.target.closest('[data-entity-drawer-close]');
    if (closer !== null) {
        const drawer = closer.closest('wa-drawer');
        if (drawer !== null) drawer.open = false;
    }
});

document.querySelectorAll('[data-entity-create-form]').forEach(form => {
    form.addEventListener('submit', async event => {
        event.preventDefault();
        const drawer = form.closest('wa-drawer');
        const errorBox = form.querySelector('[data-entity-create-errors]');
        const submitButton = drawer?.querySelector('[data-entity-create-submit]') ?? null;
        errorBox?.classList.add('d-none');
        if (submitButton !== null) submitButton.disabled = true;

        try {
            const response = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            const payload = await response.json();
            refreshCsrfTokens(payload.csrf);
            if (!response.ok || payload.success !== true) {
                throw new Error(Object.values(payload.errors ?? {}).join(' ') || 'La fiche n’a pas pu être créée.');
            }

            const select = document.getElementById(form.dataset.targetSelect);
            if (select === null) throw new Error('Le sélecteur à actualiser est introuvable.');
            const newValue = String(payload.entity.id);
            const selectIds = [form.dataset.targetSelect, ...(form.dataset.mirrorSelects ?? '').split(',')]
                .map(value => value.trim()).filter(Boolean);
            for (const selectId of new Set(selectIds)) {
                const relatedSelect = document.getElementById(selectId);
                if (relatedSelect === null) continue;
                const option = document.createElement('wa-option');
                // Web Awesome lit la valeur déclarative de wa-option depuis
                // son attribut HTML. La propriété seule n'est pas fiable sur
                // toutes les versions du composant.
                option.setAttribute('value', newValue);
                option.textContent = payload.entity.label;
                if (relatedSelect === select) option.setAttribute('selected', '');
                relatedSelect.append(option);
            }

            const selectedAttributeValues = [...select.querySelectorAll('wa-option[selected]')]
                .map(option => option.getAttribute('value'))
                .filter(value => value !== null)
                .map(String);
            const componentValues = Array.isArray(select.value)
                ? select.value.map(String)
                : (select.value ? [String(select.value)] : []);
            select.value = select.multiple
                ? [...new Set([...componentValues, ...selectedAttributeValues, newValue])]
                : newValue;

            // Web Awesome applique la valeur d'un composant après la mise à
            // jour de ses options. Attendre son cycle de rendu garantit que
            // les consommateurs dépendants (par exemple la présidence d'un
            // club dérivée de ses membres) lisent déjà la nouvelle sélection.
            if (select.updateComplete instanceof Promise) await select.updateComplete;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            select.dispatchEvent(new CustomEvent('manager-options-changed', {
                bubbles: true,
                detail: { id: newValue, label: payload.entity.label },
            }));
            form.reset();
            if (drawer !== null) drawer.open = false;
            select.focus();
        } catch (error) {
            if (errorBox !== null) {
                errorBox.textContent = error instanceof Error ? error.message : 'La fiche n’a pas pu être créée.';
                errorBox.classList.remove('d-none');
                errorBox.focus();
            }
        } finally {
            if (submitButton !== null) submitButton.disabled = false;
        }
    });
});
